import { ChevronLeft, House } from 'lucide-react';
import { useMemo } from 'react';
import { AppPanelSidebarItems } from '@/components/desktop-shell/app-panel-sidebar';
import { libraryArtifacts, shortAgo } from '@/components/library-page/library-model';
import {
  SidebarError,
  SidebarHeader,
  SidebarItem,
  SidebarSection,
} from '@/components/sidebar/sidebar';
import {
  type AppSection,
  appSections,
  fullScreenSectionsKey,
  useAppSectionItems,
  useLiveSections,
} from '@/features/apps/app-panel-sidebar';
import { groupedSections, sendToRemoteApp, useRemoteApp } from '@/features/apps/remote-app-bridge';
import { useArtifactIndex } from '@/features/apps/use-artifact-index';
import { pluginIcon } from '@/features/plugins/plugin-icons';
import type { PluginPanel } from '@/features/plugins/plugin-workbench';
import { openAppHome, useAppHome } from '@/lib/app-home';
import { useLibraryPage } from '@/lib/navigation';
import { formatSidebarAge } from '@/lib/sidebar-threads';

/**
 * The host's left column while an app is full screen (the extensions proposal's "Le cadre"): "‹
 * Assistants" at its head (back to them), then the app's sections: its home and the user's items
 * of it, the live sections of what its view shows (`page.sections.set`), and the sections of its
 * app (`page.sidebars`), each item opening at its address (`/apps/<plugin>/<section>?item=`).
 */
export function AppHomeColumn({
  plugin,
  panel,
  density = 'desktop',
}: {
  plugin: string;
  panel: PluginPanel | null;
  density?: 'desktop' | 'drawer';
}) {
  const library = useLibraryPage();
  const key = fullScreenSectionsKey(plugin);
  const live = useLiveSections(key);
  return (
    <div data-app-column={plugin} data-density={density}>
      <SidebarSection>
        <SidebarItem
          data-app-column-back
          icon={ChevronLeft}
          label="Assistants"
          onSelect={() => library.leave()}
        />
      </SidebarSection>
      {panel?.remoteView !== undefined ? (
        <RemoteAppSections bridgeKey={key} panel={panel} />
      ) : (
        panel !== null && (
          <>
            <AppHomeSidebarItems panel={panel} />
            {live.length > 0 && (
              <AppPanelSidebarItems liveSections={live} panel={panel} section={null} tabId={key} />
            )}
            {appSections(panel).map((section) => (
              <FullScreenSection key={section.id} panel={panel} section={section} />
            ))}
          </>
        )
      )}
    </div>
  );
}

/**
 * A Crowlingo application full screen (CrowDrive, CrowBox): the sections its page sends through
 * the embed bridge, by group, the shown one active; a pick reaches the page, which goes there.
 */
function RemoteAppSections({ panel, bridgeKey }: { panel: PluginPanel; bridgeKey: string }) {
  const { nav } = useRemoteApp(bridgeKey);
  const groups = groupedSections(nav);
  return groups.length === 0 ? (
    <SidebarHeader busy title={panel.title} />
  ) : (
    groups.map(({ group, sections }, index) => (
      <div data-remote-sections={group ?? ''} key={group ?? ''}>
        <SidebarHeader title={group ?? (index === 0 ? panel.title : '')} />
        <SidebarSection aria-label={`${panel.title} · ${group ?? panel.title}`}>
          {sections.map((section) => (
            <SidebarItem
              active={section.active === true}
              detail={section.count}
              {...(section.icon === undefined ? {} : { icon: pluginIcon(section.icon) })}
              key={section.id}
              label={section.label}
              onSelect={() =>
                sendToRemoteApp(bridgeKey, { type: 'crow:navigate', section: section.id })
              }
              title={section.label}
            />
          ))}
        </SidebarSection>
      </div>
    ))
  );
}

/**
 * A section of the app (`page.sidebars`) full screen: its items, read with no conversation. An
 * empty section is not shown (no heading over nothing, no helper text); a section named like the
 * app goes on under the app's own heading rather than repeating it.
 */
function FullScreenSection({ panel, section }: { panel: PluginPanel; section: AppSection }) {
  const home = useAppHome();
  const listed = useAppSectionItems(panel, section, { workspace: null });
  const ofSection = home?.route?.split('/').filter(Boolean)[0] === section.id;
  if (listed.error === null && listed.items.length === 0) return null;
  const sameTitle = section.title.trim().toLowerCase() === panel.title.trim().toLowerCase();
  return (
    <div data-app-section={section.id}>
      {!sameTitle && <SidebarHeader busy={listed.loading} title={section.title} />}
      {listed.error !== null && <SidebarError>{listed.error}</SidebarError>}
      {listed.items.length > 0 && (
        <SidebarSection aria-label={`${panel.title} · ${section.title}`}>
          {listed.items.map((entry) => (
            <SidebarItem
              active={ofSection && home?.item === entry.id}
              detail={
                entry.detail ??
                (entry.updatedAt === undefined ? undefined : formatSidebarAge(entry.updatedAt))
              }
              {...(entry.icon ? { icon: pluginIcon(entry.icon) } : {})}
              key={entry.id}
              label={entry.title}
              onSelect={() => openAppHome(panel.plugin, entry.id, { route: `/${section.id}` })}
              title={entry.title}
            />
          ))}
        </SidebarSection>
      )}
    </div>
  );
}

/**
 * The app's home and the user's items of it, made in any conversation, the last changed first.
 * One opens on the app's page; "Home" goes back to the templates and examples.
 */
export function AppHomeSidebarItems({ panel }: { panel: PluginPanel }) {
  const home = useAppHome();
  const index = useArtifactIndex(undefined);
  const items = useMemo(
    () => libraryArtifacts(index.artifacts).filter((artifact) => artifact.plugin === panel.plugin),
    [index.artifacts, panel.plugin],
  );
  const icon = pluginIcon(panel.icon ?? 'app');
  return (
    <>
      <SidebarHeader busy={index.loading} title={panel.title} />
      <SidebarSection>
        <SidebarItem
          active={home?.item === null && home.route === null}
          icon={House}
          label="Home"
          onSelect={() => openAppHome(panel.plugin)}
        />
      </SidebarSection>
      {index.error !== null && items.length === 0 && <SidebarError>{index.error}</SidebarError>}
      {items.length > 0 && (
        <SidebarSection>
          {items.map((artifact) => (
            <SidebarItem
              active={home?.route === null && home?.item === artifact.id}
              detail={shortAgo(artifact.modifiedAt)}
              icon={icon}
              key={artifact.id}
              label={artifact.title}
              onSelect={() => openAppHome(panel.plugin, artifact.id)}
              title={artifact.title}
            />
          ))}
        </SidebarSection>
      )}
    </>
  );
}
